<script setup lang="ts">
import AppLayout from '../../layouts/AppLayout.vue'
import { ref, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAuth } from '../../composables/useAuth'
import { useCurrentContext } from '../../composables/useCurrentContext'
import { API_CONFIG } from '../../config/api'

const route = useRoute()
const router = useRouter()
const { supplierId } = useCurrentContext()
const { getAccessTokenSilently } = useAuth()
const loading = ref(true)
const promotions = ref<any[]>([])
const showModal = ref(false)
const isEditing = ref(false)
const editingId = ref<string | null>(null)

const form = ref({
    name: '',
    description: '',
    scope: 'PRODUCT',
    tsd_name: '',
    valid_until: ''
})

async function fetchPromos() {
    loading.value = true;
    try {
        const token = await getAccessTokenSilently()
        const res = await fetch(`${API_CONFIG.baseURL}/api/suppliers/${supplierId.value}/promotions`, {
            headers: { Authorization: `Bearer ${token}` }
        })
        promotions.value = await res.json()
    } catch (e) {
        console.error(e)
    } finally {
        loading.value = false
    }
}

function openCreateModal() {
    isEditing.value = false
    editingId.value = null
    form.value = {
        name: '',
        description: '',
        scope: 'PRODUCT',
        tsd_name: '',
        valid_until: ''
    }
    showModal.value = true
}

function openEditModal(promo: any) {
    isEditing.value = true
    editingId.value = promo.id
    form.value = {
        name: promo.name,
        description: promo.description,
        scope: promo.scope,
        tsd_name: promo.tsd_name || '',
        valid_until: (promo.valid_until ? new Date(promo.valid_until).toISOString().split('T')[0] : '') as string
    }
    showModal.value = true
}

async function savePromo() {
    try {
        const token = await getAccessTokenSilently()
        const url = isEditing.value 
            ? `${API_CONFIG.baseURL}/api/suppliers/${supplierId.value}/promotions/${editingId.value}`
            : `${API_CONFIG.baseURL}/api/suppliers/${supplierId.value}/promotions`
            
        const method = isEditing.value ? 'PUT' : 'POST'

        await fetch(url, {
            method: method,
            headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(form.value)
        })
        showModal.value = false;
        fetchPromos();
    } catch(e) {
        alert('Error saving promo')
    }
}

async function deletePromo(id: string) {
    if (!confirm('Are you sure you want to delete this promotion?')) return

    try {
        const token = await getAccessTokenSilently()
        await fetch(`${API_CONFIG.baseURL}/api/suppliers/${supplierId.value}/promotions/${id}`, {
            method: 'DELETE',
            headers: { Authorization: `Bearer ${token}` }
        })
        fetchPromos()
    } catch(e) {
        alert('Error deleting promotion')
    }
}

onMounted(() => {
    fetchPromos()

    // Auto-open create modal if ?create=true query param is present
    if (route.query.create === 'true') {
        openCreateModal()
        // Clear the query param to prevent re-opening on refresh
        router.replace({ path: route.path, query: {} })
    }
})

const formatDate = (d: string) => new Date(d).toLocaleDateString()
</script>

<template>
  <AppLayout>
    <div class="space-y-6">
       <div class="flex justify-between items-center">
        <div>
           <h1 class="text-2xl font-bold text-cc-text">Promotions</h1>
           <p class="text-cc-text-secondary">Current active campaigns and spiffs.</p>
        </div>
        <button @click="openCreateModal" class="bg-cc-primary text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-cc-primary-hover">
            + New Promo
        </button>
      </div>

      <div v-if="loading" class="text-center py-12 text-gray-500">Loading promotions...</div>

      <div v-else class="space-y-4">
        <div v-for="promo in promotions" :key="promo.id" class="bg-cc-elevated p-6 rounded-lg border border-cc-border shadow-sm flex flex-col md:flex-row gap-6 relative overflow-hidden group">
            <!-- Active Indicator -->
            <div class="absolute left-0 top-0 bottom-0 w-1 bg-green-500" v-if="!promo.valid_until || new Date(promo.valid_until) > new Date()"></div>
            <div class="absolute left-0 top-0 bottom-0 w-1 bg-gray-300" v-else></div>

            <div class="flex-1">
                <div class="flex items-center gap-2 mb-1">
                   <h3 class="text-lg font-bold text-cc-text">{{ promo.name }}</h3>
                   <span v-if="!promo.valid_until || new Date(promo.valid_until) > new Date()" class="text-[10px] bg-green-100 text-green-700 px-2 py-0.5 rounded uppercase font-bold">Active</span>
                   <span v-else class="text-[10px] bg-gray-100 text-gray-600 px-2 py-0.5 rounded uppercase font-bold">Expired</span>
                   
                   <span class="text-[10px] px-2 py-0.5 rounded uppercase font-bold border ml-2" 
                    :class="{
                        'bg-blue-50 text-blue-700 border-blue-200': promo.scope === 'PRODUCT',
                        'bg-indigo-50 text-indigo-700 border-indigo-200': promo.scope === 'SUPPLIER',
                        'bg-amber-50 text-amber-700 border-amber-200': promo.scope === 'TSD'
                    }">
                       {{ promo.scope === 'TSD' && promo.tsd_name ? `TSD: ${promo.tsd_name}` : promo.scope }}
                   </span>
                </div>
                <p class="text-cc-text-secondary">{{ promo.description }}</p>
                <div class="mt-4 flex flex-wrap gap-2">
                    <span v-for="p in promo.products" :key="p.id" class="text-xs bg-gray-50 dark:bg-gray-700 border border-gray-100 dark:border-gray-600 px-2 py-1 rounded text-cc-text-secondary">
                        {{ p.name }}
                    </span>
                </div>
            </div>
            
            <div class="shrink-0 flex flex-col items-end justify-between">
                <div class="text-sm">
                    <div class="text-cc-text-secondary text-right">Valid Until</div>
                    <div class="font-medium text-cc-text text-right">{{ promo.valid_until ? formatDate(promo.valid_until) : 'Ongoing' }}</div>
                </div>

                <!-- Actions -->
                <div class="flex gap-2 mt-4 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button @click="openEditModal(promo)" class="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg" title="Edit">
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"/></svg>
                    </button>
                    <button @click="deletePromo(promo.id)" class="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg" title="Delete">
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/></svg>
                    </button>
                </div>
            </div>
        </div>
        
        <div v-if="promotions.length === 0" class="text-center py-8 text-gray-400 bg-cc-elevated rounded border border-dashed border-cc-border">
            No promotions created yet.
        </div>
      </div>

      <!-- Create/Edit Modal -->
      <div v-if="showModal" class="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div class="bg-cc-elevated rounded-xl shadow-xl w-full max-w-lg p-6">
              <h2 class="text-xl font-bold mb-4 dark:text-white">{{ isEditing ? 'Edit Promotion' : 'Create New Promotion' }}</h2>
              <form @submit.prevent="savePromo" class="space-y-4">
                  <div>
                      <label class="block text-sm font-medium text-cc-text-secondary mb-1">Promo Name</label>
                      <input v-model="form.name" required class="w-full px-3 py-2 rounded-lg border border-cc-border-med bg-white dark:bg-gray-900 text-cc-text" />
                  </div>
                  <div>
                      <label class="block text-sm font-medium text-cc-text-secondary mb-1">Description</label>
                      <textarea v-model="form.description" required rows="2" class="w-full px-3 py-2 rounded-lg border border-cc-border-med bg-white dark:bg-gray-900 text-cc-text"></textarea>
                  </div>
                  <div class="grid grid-cols-2 gap-4">
                      <div>
                          <label class="block text-sm font-medium text-cc-text-secondary mb-1">Scope</label>
                          <select v-model="form.scope" class="w-full px-3 py-2 rounded-lg border border-cc-border-med bg-white dark:bg-gray-900 text-cc-text">
                              <option value="PRODUCT">Product Specific</option>
                              <option value="SUPPLIER">All Products (Supplier Wide)</option>
                              <option value="TSD">TSD Specific</option>
                          </select>
                      </div>
                      <div v-if="form.scope === 'TSD'">
                          <label class="block text-sm font-medium text-cc-text-secondary mb-1">TSD Name</label>
                          <input v-model="form.tsd_name" placeholder="e.g. Avant" class="w-full px-3 py-2 rounded-lg border border-cc-border-med bg-white dark:bg-gray-900 text-cc-text" />
                      </div>
                  </div>
                  <div>
                      <label class="block text-sm font-medium text-cc-text-secondary mb-1">End Date</label>
                      <input type="date" v-model="form.valid_until" class="w-full px-3 py-2 rounded-lg border border-cc-border-med bg-white dark:bg-gray-900 text-cc-text dark:[color-scheme:dark]" />
                  </div>
                  
                  <div class="flex justify-end gap-3 mt-6">
                      <button type="button" @click="showModal = false" class="px-4 py-2 text-cc-text-secondary hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">Cancel</button>
                      <button type="submit" class="px-4 py-2 bg-cc-primary text-white rounded-lg hover:bg-cc-primary-hover">{{ isEditing ? 'Update' : 'Create' }}</button>
                  </div>
              </form>
          </div>
      </div>
    </div>
  </AppLayout>
</template>
